import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Button,
  Avatar,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonPinCircleOutlinedIcon from "@mui/icons-material/PersonPinCircleOutlined";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { CashkaroService } from "../../../core/services";
import { CashkaroLink } from "./types";
import { CC, CashkaroOutletContext } from "./CashkaroLayout";

// Icon-tile action button — same flat, softly-tinted treatment as the Zap
// Storefront products table's row actions, so the two "manage your links"
// surfaces read as one family.
const RowActionButton: React.FC<{
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onClick}
        sx={{
          bgcolor: CC.bg,
          color: active ? "success.main" : CC.textSecondary,
          "&:hover": { bgcolor: CC.primaryWash, color: CC.primaryDark },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export const CashkaroLinks: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.UserReducer?.UserData);
  const influencerId = user?.influencer_id;
  const accountId = user?.account_id;

  const { linksSearch: search, setLinksCount } = useOutletContext<CashkaroOutletContext>();
  const [links, setLinks] = useState<CashkaroLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "info" | "error"; actionTo?: string } | null>(null);

  useEffect(() => {
    if (!influencerId) return;
    CashkaroService.getMyLinks(influencerId)
      .then((res) => {
        const list = res?.data?.links;
        setLinks(Array.isArray(list) ? list : []);
      })
      .catch(() => setLinks([]))
      .finally(() => setLoading(false));
  }, [influencerId]);

  // Our own redirect domain, never the raw Trackier tracking_url (it embeds
  // our shared pub_id and internal cctm/product params in plaintext) — falls
  // back to tracking_url only for legacy rows saved before click_token existed.
  const displayUrl = (link: CashkaroLink) => (link.click_token ? CashkaroService.clickUrl(link.click_token) : link.tracking_url);

  const handleCopy = (link: CashkaroLink) => {
    navigator.clipboard.writeText(displayUrl(link));
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleAddToStorefront = async (link: CashkaroLink) => {
    if (!influencerId || !accountId) return;
    const key = `${link.id}-storefront`;
    setActionLoading(key);
    try {
      const res = await CashkaroService.addLinkToStorefront({ link_id: link.id, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setSnackbar({ message: res.data.message || "Set up your Zap Storefront first", severity: "info", actionTo: "/influencer/storefront" });
      } else {
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, on_storefront: true } : l)));
        setSnackbar({ message: res?.data?.already_added ? "Already on your Storefront" : "Added to your Storefront", severity: "success" });
      }
    } catch (e) {
      setSnackbar({ message: "Could not add to Storefront right now — please try again.", severity: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToBio = async (link: CashkaroLink) => {
    if (!influencerId || !accountId) return;
    const key = `${link.id}-bio`;
    setActionLoading(key);
    try {
      const res = await CashkaroService.addLinkToBio({ link_id: link.id, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setSnackbar({ message: res.data.message || "Set up your Zap Link in Bio page first", severity: "info", actionTo: "/influencer/link-in-bio" });
      } else {
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, on_bio: true } : l)));
        setSnackbar({ message: res?.data?.already_added ? "Already on your Bio page" : "Added to your Bio page", severity: "success" });
      }
    } catch (e) {
      setSnackbar({ message: "Could not add to Bio right now — please try again.", severity: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) => (l.campaign_title || "").toLowerCase().includes(q) || (l.label || "").toLowerCase().includes(q));
  }, [links, search]);

  // The "N links" count next to the search box lives in the tab row
  // (CashkaroLayout), not in this component — report it up whenever the
  // filtered count changes instead of duplicating the search box here.
  useEffect(() => {
    setLinksCount(filteredLinks.length);
  }, [filteredLinks.length, setLinksCount]);

  // Reset to page 1 whenever the (layout-owned) search term changes.
  useEffect(() => {
    setPage(0);
  }, [search]);

  const pagedLinks = useMemo(
    () => filteredLinks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredLinks, page, rowsPerPage]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={26} sx={{ color: CC.primary }} />
      </Box>
    );
  }

  if (links.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <LinkOffOutlinedIcon sx={{ fontSize: 60, color: "#CBD5E1", mb: 1 }} />
        <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: "1.05rem", color: CC.textPrimary }} mb={0.5}>
          No links yet
        </Typography>
        <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font }} mb={3}>
          Links you generate from a campaign will show up here.
        </Typography>
        <Button
          disableElevation
          onClick={() => navigate("/influencer/brand-deals")}
          sx={{
            bgcolor: "#000",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontFamily: CC.font,
            borderRadius: "999px",
            px: 3,
            "&:hover": { bgcolor: "#111" },
          }}
        >
          Browse campaigns
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
        {filteredLinks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font }}>No links match "{search}".</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: CC.bg,
                        fontWeight: 700,
                        fontFamily: CC.font,
                        border: 0,
                        borderBottom: `1px solid ${CC.border}`,
                        color: CC.textPrimary,
                      },
                    }}
                  >
                    <TableCell>Campaign</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Link</TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Created</TableCell>
                    <TableCell align="right">Post to</TableCell>
                    <TableCell align="right">Copy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                  {pagedLinks.map((link) => {
                    const onStorefront = !!link.on_storefront;
                    const onBio = !!link.on_bio;
                    const initial = (link.campaign_title || link.label || "?")[0].toUpperCase();
                    return (
                      <TableRow key={link.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: CC.primaryWash, color: CC.primaryDark, fontWeight: 700, flexShrink: 0 }}>
                              {initial}
                            </Avatar>
                            <Box minWidth={0}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220, color: CC.textPrimary }} title={link.campaign_title || undefined}>
                                {link.campaign_title || `Campaign #${link.campaign_id}`}
                              </Typography>
                              <Typography variant="caption" noWrap sx={{ display: "block", color: CC.textSecondary, maxWidth: 220 }}>
                                {link.label || "—"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260, display: { xs: "none", md: "table-cell" } }}>
                          <Tooltip title={displayUrl(link)}>
                            <MuiLink
                              href={displayUrl(link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: CC.primaryDark }}
                            >
                              {displayUrl(link)}
                            </MuiLink>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: CC.textSecondary, display: { xs: "none", sm: "table-cell" } }}>
                          {new Date(link.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={0.5}>
                            <RowActionButton
                              title={onStorefront ? "Already on your Storefront" : "Add to Storefront"}
                              active={onStorefront}
                              disabled={onStorefront || actionLoading === `${link.id}-storefront`}
                              onClick={() => handleAddToStorefront(link)}
                            >
                              {actionLoading === `${link.id}-storefront` ? <CircularProgress size={16} /> : onStorefront ? <CheckRoundedIcon fontSize="small" /> : <StorefrontOutlinedIcon fontSize="small" />}
                            </RowActionButton>
                            <RowActionButton
                              title={onBio ? "Already on your Bio page" : "Add to Link in Bio"}
                              active={onBio}
                              disabled={onBio || actionLoading === `${link.id}-bio`}
                              onClick={() => handleAddToBio(link)}
                            >
                              {actionLoading === `${link.id}-bio` ? <CircularProgress size={16} /> : onBio ? <CheckRoundedIcon fontSize="small" /> : <PersonPinCircleOutlinedIcon fontSize="small" />}
                            </RowActionButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <RowActionButton title={copiedId === link.id ? "Copied!" : "Copy link"} active={copiedId === link.id} onClick={() => handleCopy(link)}>
                            {copiedId === link.id ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                          </RowActionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <TablePagination
              component="div"
              count={filteredLinks.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ fontFamily: CC.font, borderTop: `1px solid ${CC.border}` }}
            />
          </>
        )}
      </Paper>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            action={
              snackbar.actionTo ? (
                <Button color="inherit" size="small" onClick={() => navigate(snackbar.actionTo!)} sx={{ textTransform: "none", fontWeight: 600 }}>
                  Set up
                </Button>
              ) : undefined
            }
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CashkaroLinks;
